import React, { useState, useEffect } from "react";
import {
  Card,
  Text,
  Group,
  Stack,
  Avatar,
  Title,
  Loader,
  Alert,
  Box,
  Button,
  Modal,
  MultiSelect,
} from "@mantine/core";
import { IconAlertCircle, IconEdit } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";

const Permissions = () => {
  const { getVerifiedJWT } = useAuth();
  const [permissionsData, setPermissionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedManagerIds, setSelectedManagerIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fetch permissions data from API
  const fetchTrainingPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v1/company/permissions/training"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.data || [];
        setPermissionsData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch training permissions");
      }
    } catch (err) {
      setError("Failed to fetch training permissions");
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the edit modal
  const handleEditClick = () => {
    const managersWithPermissions = permissionsData
      .filter(item => item.is_manager && item.has_training_permission)
      .map(item => item.user_id.toString());
    setSelectedManagerIds(managersWithPermissions);
    setEditModalOpen(true);
  };

  // Handle saving permissions
  const handleSave = async () => {
    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v1/company/update-permissions"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            user_ids: selectedManagerIds.map(id => parseInt(id)),
            permission_type: "training",
          }),
        }
      );

      if (response.ok) {
        setEditModalOpen(false);
        await fetchTrainingPermissions(); // Refresh the list
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update permissions");
      }
    } catch (err) {
      setError("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchTrainingPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter data for display and multiselect
  const managersWithPermissions = permissionsData.filter(
    item => item.is_manager && item.has_training_permission
  );

  const allManagers = permissionsData.filter(item => item.is_manager);

  // Prepare multiselect data
  const managerSelectData = allManagers.map(item => ({
    value: item.user_id.toString(),
    label: `${item.user.first_name} ${item.user.last_name}`,
  }));

  const renderManagerItem = item => (
    <Group key={item.user_id} align="center" gap="md" py="xs">
      <Avatar
        src={item.user_profile_image}
        size="sm"
        radius="xl"
        alt={`${item.user.first_name} ${item.user.last_name}`}
      >
        {item.user.first_name?.charAt(0)?.toUpperCase() || "M"}
      </Avatar>
      <Text size="sm" fw={500}>
        {`${item.user.first_name} ${item.user.last_name}`}
      </Text>
    </Group>
  );

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Permissions</Title>
        </Group>

        <Stack gap="md">
          {/* Training Section */}
          <div>
            <Group justify="space-between" align="flex-start" mb="md">
              <Box style={{ flex: 1 }}>
                <Title order={4}>Training</Title>
                <Text size="sm" c="dimmed">
                  Allow managers to create their own training videos and
                  courses.
                </Text>
              </Box>
              <Button
                leftSection={<IconEdit size={16} />}
                variant="outline"
                size="sm"
                onClick={handleEditClick}
                disabled={loading}
              >
                Edit
              </Button>
            </Group>

            {loading ? (
              <Group justify="center" py="xl">
                <Loader size="md" />
                <Text c="dimmed">Loading training permissions...</Text>
              </Group>
            ) : error ? (
              <Alert icon={<IconAlertCircle size="1rem" />} color="red" mb="md">
                {error}
              </Alert>
            ) : managersWithPermissions.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No managers have training permissions yet
              </Text>
            ) : (
              <Stack gap="sm">
                {managersWithPermissions.map(renderManagerItem)}
              </Stack>
            )}
          </div>
        </Stack>
      </Card>

      {/* Edit Training Permissions Modal */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Training Permissions"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select which managers should have training permissions:
          </Text>

          <MultiSelect
            label="Managers with Training Permissions"
            placeholder="Select managers"
            data={managerSelectData}
            value={selectedManagerIds}
            onChange={setSelectedManagerIds}
            searchable
            size="md"
          />

          <Group justify="flex-end" gap="md" mt="lg">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default Permissions;
